import React, { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack
} from '@mui/material';
import ResponsiveTitle from '../components/ResponsiveTitle';
import { LocalOffer as LocalOfferIcon } from '@mui/icons-material';
import PageLayout from '../components/PageLayout';
import { useDeals } from '../hooks/useDeals';
import { Deal } from '../services/deals';
import DealCard, { renderStoreIcon, renderStoreLabel } from '../components/DealCard';

const Deals = () => {
  const { freeGames, loadingFreeGames, freeGamesError, lastUpdated } = useDeals();
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);

  const handleDealClick = (deal: Deal) => {
    if (deal.otherDeals && deal.otherDeals.length > 0) {
      setSelectedDeal(deal);
    } else {
      window.open(deal.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCloseDeal = () => {
    setSelectedDeal(null);
  };

  return (
    <PageLayout>
      <Container maxWidth="xl">
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 4,
          mt: 4,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          borderRadius: 4,
          px: 3,
          py: 2,
          color: 'white',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LocalOfferIcon />
            <ResponsiveTitle variant="h4" minFontSize={16} maxFontSize={34} style={{ fontWeight: 'bold' }}>
              Bonnes Affaires
            </ResponsiveTitle>
          </Box>

          {lastUpdated && (
            <Typography variant="body2" sx={{ color: '#aaa', fontStyle: 'italic' }}>
              Mise à jour : {lastUpdated.toLocaleString('fr-FR', {
                day: 'numeric',
                month: 'long',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>
          )}
        </Box>

        {loadingFreeGames ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress size={40} />
          </Box>
        ) : freeGamesError ? (
          <Typography color="error" sx={{ textAlign: 'center', p: 2 }}>
            Erreur : {freeGamesError}
          </Typography>
        ) : freeGames.length === 0 ? (
          <Typography sx={{ textAlign: 'center', p: 2, color: '#aaa' }}>
            Aucune offre disponible pour le moment.
          </Typography>
        ) : (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '1fr 1fr 1fr',
              lg: '1fr 1fr 1fr 1fr'
            },
            gap: 3
          }}>
            {freeGames.map((game, idx) => (
              <DealCard
                key={idx}
                deal={game}
                onClick={handleDealClick}
              />
            ))}
          </Box>
        )}

        {/* Dialog de sélection d'offre */}
        <Dialog
          open={!!selectedDeal}
          onClose={handleCloseDeal}
          maxWidth="sm"
          fullWidth
        >
          {selectedDeal && (
            <>
              <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                {selectedDeal.title}
              </DialogTitle>
              <DialogContent sx={{ mt: 2 }}>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Sélectionnez une boutique pour voir l'offre :
                </Typography>
                <Stack spacing={2}>
                  {/* Liste de toutes les offres (Principale + Alternatives) */}
                  {[selectedDeal, ...(selectedDeal.otherDeals || [])].map((deal, index) => (
                    <Button
                      key={index}
                      variant="contained"
                      size="large"
                      href={deal.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={renderStoreIcon(deal.source)}
                      sx={{
                        justifyContent: 'space-between',
                        px: 3,
                        py: 1.5
                      }}
                    >
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {renderStoreLabel(deal.source)}
                      </Box>
                      <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {deal.price === 0 ? 'GRATUIT' : `${deal.price?.toFixed(2)}€`}
                      </Box>
                    </Button>
                  ))}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDeal} variant="outlined">
                  Fermer
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

      </Container>
    </PageLayout>
  );
};

export default Deals;
